import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import useMasterData from '@shared/hooks/useMasterData';
import { useStore } from '@shared/hooks/useStore';
import { showQuickViewModal } from '@user/components/publication/QuickViewModal';
import FeaturedCarousel from '@user/components/publication/FeaturedCarousel';
import PublicationFilterSidebar from '@user/pages/publications/components/PublicationFilterSidebar';
import PublicationCard from '@shared/components/common/PublicationCard';
import Pagination from '@shared/components/pagination/Pagination';
import { ROUTES } from '@shared/configs/constants';
import { PUBLICATION_TYPE } from '@shared/types/enums';
import { Publication } from '@shared/types/publication';

enum PAGE_TYPE {
    RANKING = 'ranking',
    RECENTLY = 'recently',
    BOOKS = 'books',
    DEFAULT = 'default'
}

const PublicationsPage = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { modalStore, publicationStore, apiStore } = useStore();
    const { publications, newPublications: featuredPublications, totalRecord } = publicationStore;
    const isLoading = apiStore.isLoading;

    const pageType = (() => {
        switch (location.pathname) {
            case ROUTES.ranking.href: return PAGE_TYPE.RANKING;
            case ROUTES.recently.href: return PAGE_TYPE.RECENTLY;
            case ROUTES.books.href: return PAGE_TYPE.BOOKS;
            default: return PAGE_TYPE.DEFAULT;
        }
    })();

    // Filters
    const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
        const params = new URLSearchParams(window.location.search);
        const catParam = params.get('categoryCodes');
        return catParam ? catParam.split(',') : [];
    });
    const [priceRange, setPriceRange] = useState({ from: '', to: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchKeyword, setSearchKeyword] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('search') || '';
    });

    const { categories } = useMasterData();

    const pageSize = useMemo(() => {
        switch (pageType) {
            case PAGE_TYPE.RECENTLY:
                return 24;
            default:
                return 20;
        }
    }, [pageType]);

    const fetchFeatured = async () => {
        publicationStore.fetchNewPublications({ size: 10 });
    };

    const fetchPublications = useCallback(async (page: number, categoryCodes?: string[], prices?: { from: string, to: string }, keyword?: string) => {
        const params: any = {
            page: page,
            size: pageSize
        };

        if (categoryCodes && categoryCodes.length > 0 && !categoryCodes.includes('all')) {
            params.categoryCodes = categoryCodes.join(',');
        }

        if (prices?.from) params.minPrice = Number(prices.from);
        if (prices?.to) params.maxPrice = Number(prices.to);
        if (keyword) params.keyword = keyword;

        switch (pageType) {
            case PAGE_TYPE.RANKING:
                publicationStore.fetchRankingPublications(params);
                break;
            case PAGE_TYPE.RECENTLY:
                publicationStore.fetchRecentlyViewedPublications({ page: params.page, size: 24 });
                break;
            case PAGE_TYPE.BOOKS:
                publicationStore.fetchPublications({ ...params, type: PUBLICATION_TYPE.Book });
                break;
            default:
                publicationStore.fetchPublications(params);
        }
    }, [pageType, publicationStore]);

    useEffect(() => {
        fetchFeatured();
    }, []);

    useEffect(() => {
        const catParam = searchParams.get('categoryCodes');
        const searchParam = searchParams.get('search');

        setSelectedCategories(catParam ? catParam.split(',') : []);
        setSearchKeyword(searchParam || '');
        setCurrentPage(1);
    }, [searchParams, location.pathname]);

    useEffect(() => {
        fetchPublications(currentPage, selectedCategories, priceRange, searchKeyword);
    }, [currentPage, selectedCategories, priceRange, searchKeyword, location.pathname]);

    const handleCategoryToggle = (categoryCode: string) => {
        if (categoryCode === 'all') {
            setSelectedCategories([]);
            return;
        }

        setSelectedCategories(prev => (
            prev.includes(categoryCode) ?
                prev.filter(c => c !== categoryCode) :
                [...prev, categoryCode]
        ));
        setCurrentPage(1);
    };

    const handlePriceChange = (field: 'from' | 'to', value: string) => {
        setPriceRange(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleQuickView = (publication: Publication) => {
        showQuickViewModal(publication, modalStore);
    };

    const handlePublicationClick = (publication: Publication) => {
        navigate(ROUTES.publicationDetail.href.replace(':id', publication.id));
    };

    const displayPublications = (pageType === PAGE_TYPE.RANKING ?
        publicationStore.rankingPublications :
        pageType === PAGE_TYPE.RECENTLY ?
            publicationStore.recentlyViewed :
            publications) || [];

    const displayTotal = (pageType === PAGE_TYPE.RANKING ?
        publicationStore.totalRanking :
        pageType === PAGE_TYPE.RECENTLY ?
            publicationStore.totalRecentlyViewed :
            totalRecord) || 0;

    return (
        <>
            {featuredPublications.length > 0 && (
                <FeaturedCarousel items={featuredPublications} />
            )}

            <section className='container mx-auto px-4 py-8 md:py-10'>
                <div className='flex flex-col lg:flex-row gap-6 md:gap-12'>
                    {pageType !== PAGE_TYPE.RECENTLY && (
                        <PublicationFilterSidebar
                            categories={categories.filter(c => c.code !== 'all')}
                            selectedCategories={selectedCategories}
                            priceRange={priceRange}
                            onCategoryChange={handleCategoryToggle}
                            onPriceChange={handlePriceChange}
                        />
                    )}

                    {/* Main Content */}
                    <div className='flex-1'>
                        {isLoading && displayPublications.length === 0 ?
                            (
                                <></>
                            ) :
                            (
                                <>
                                    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 ${pageType === PAGE_TYPE.RECENTLY ?
                                        'lg:grid-cols-5 xl:grid-cols-6 gap-[24px] lg:gap-[36px] xl:gap-[48px]' :
                                        'xl:grid-cols-5 gap-x-6 gap-y-10'}`}
                                    >
                                        {displayPublications.map((publication, index) => (
                                            <PublicationCard
                                                key={publication.id}
                                                publication={publication}
                                                onQuickView={handleQuickView}
                                                onClick={handlePublicationClick}
                                                rank={pageType === PAGE_TYPE.RANKING ? (currentPage - 1) * 20 + index + 1 : undefined}
                                            />
                                        ))}
                                    </div>

                                    <div className='mt-12'>
                                        <Pagination
                                            type='full-pagination'
                                            currentPage={currentPage}
                                            totalRecords={displayTotal}
                                            pageSize={pageSize}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>

                                    {displayPublications.length === 0 && (
                                        <div className='text-center py-20 text-text-secondary'>
                                            該当する商品が見つかりませんでした。
                                        </div>
                                    )}
                                </>
                            )}
                    </div>
                </div>
            </section>
        </>
    );
});

export default PublicationsPage;
